import { useContext, useEffect, useState } from 'react';
import { ApiContext } from '../App';
import { useQuery } from '@tanstack/react-query';

import { getAccountStats } from '../lib/actions/accounts';
import { getUser } from '../lib/actions/users';
import {
  getAccountApplications,
  getAccountDocuments,
} from '../lib/actions/accounts';

function useAccountDependencies(account) {
  let accountProps;
  const api = useContext(ApiContext);
  const [hasErrors, setHasErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fetchConfig = {
    enabled: !!account && !accountProps,
    retry: false,
  };

  const {
    data: user,
    error: userError,
    isLoading: userLoading,
  } = useQuery({
    queryKey: ['users', account?.id],
    queryFn: () => getUser(api, account?.user?.id),
    ...fetchConfig,
  });

  const {
    data: applications,
    error: applicationsError,
    isLoading: applicationsLoading,
  } = useQuery({
    queryKey: ['applications', account?.id],
    queryFn: () => getAccountApplications(api, account),
    ...fetchConfig,
  });

  const {
    data: stats,
    error: statsError,
    isLoading: statsLoading,
  } = useQuery({
    queryKey: ['stats', account?.id],
    queryFn: () => getAccountStats(api, account?.id),
    ...fetchConfig,
  });

  const {
    data: accountDocs,
    error: accountDocsError,
    isLoading: accountDocsLoading,
  } = useQuery({
    queryKey: ['accountDocs', account?.id],
    queryFn: () => getAccountDocuments(api, account),
    ...fetchConfig,
  });

  useEffect(() => {
    setHasErrors(
      userError || applicationsError || statsError || accountDocsError,
    );
  }, [userError, applicationsError, statsError, accountDocsError]);

  useEffect(() => {
    setIsLoading(
      userLoading || applicationsLoading || statsLoading || accountDocsLoading,
    );
  }, [userLoading, applicationsLoading, statsLoading, accountDocsLoading]);

  const errors = {
    userError,
    applicationsError,
    statsError,
    documentError: accountDocsError,
  };

  accountProps = {
    type: 'account',
    entity: account,
    ownerAddress: user?.attributes?.address,
    applications: applications?.map((app) => app.attributes),
    stats: stats,
    documents: accountDocs,
    errors,
    hasErrors,
    isLoading,
  };

  return { accountProps };
}

export default useAccountDependencies;
